import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Check, Trash2, User, Phone, Calendar } from 'lucide-react';
import { contactApi } from '@/services/api';
import { toast } from 'sonner';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await contactApi.getAll();
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await contactApi.markRead(id);
      setContacts((prev) => prev.map((c) => c.id === id ? { ...c, read: true } : c));
      if (selectedContact?.id === id) setSelectedContact((prev) => ({ ...prev, read: true }));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await contactApi.remove(id);
      toast.success('Contact deleted');
      if (selectedContact?.id === id) setSelectedContact(null);
      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-contacts">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Contact Submissions</h1>
        <p className="text-white/60">View and manage contact form submissions</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Contacts List */}
        <div className="bg-white/5 border border-white/10">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-white font-medium">All Messages ({contacts.length})</h2>
          </div>
          
          {loading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center text-white/40">
              <Mail size={40} className="mx-auto mb-4 opacity-50" />
              <p>No contact submissions yet</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10 max-h-[600px] overflow-y-auto">
              {contacts.map((contact) => (
                <motion.div
                  key={contact.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={() => {
                    setSelectedContact(contact);
                    if (!contact.read) markAsRead(contact.id);
                  }}
                  data-testid={`contact-item-${contact.id}`}
                  className={`p-4 cursor-pointer hover:bg-white/5 transition-colors ${
                    selectedContact?.id === contact.id ? 'bg-white/10' : ''
                  } ${!contact.read ? 'border-s-2 border-gold' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`font-medium ${contact.read ? 'text-white/60' : 'text-white'}`}>
                        {contact.name}
                      </p>
                      <p className="text-white/40 text-sm">{contact.email}</p>
                      <p className="text-white/40 text-xs mt-1">{formatDate(contact.created_at)}</p>
                    </div>
                    {!contact.read && (
                      <span className="w-2 h-2 rounded-full bg-gold flex-shrink-0 mt-2" />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Detail */}
        <div className="bg-white/5 border border-white/10">
          {selectedContact ? (
            <>
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-white font-medium">Message Details</h2>
                <div className="flex gap-2">
                  {!selectedContact.read && (
                    <button
                      onClick={() => markAsRead(selectedContact.id)}
                      className="p-2 text-white/60 hover:text-green-400 transition-colors"
                      title="Mark as read"
                    >
                      <Check size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(selectedContact.id)}
                    data-testid="delete-contact-btn"
                    className="p-2 text-white/60 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                    <User className="text-gold" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">{selectedContact.name}</p>
                    <p className="text-white/40 text-sm">{selectedContact.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-white/60">
                    <Phone size={16} />
                    <a href={`tel:${selectedContact.phone}`} className="hover:text-gold">
                      {selectedContact.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-white/60">
                    <Calendar size={16} />
                    <span>{formatDate(selectedContact.created_at)}</span>
                  </div>
                </div>

                {selectedContact.service && (
                  <div>
                    <p className="text-white/40 text-sm mb-1">Service</p>
                    <span className="bg-gold/20 text-gold px-3 py-1 text-sm">
                      {selectedContact.service}
                    </span>
                  </div>
                )}

                <div>
                  <p className="text-white/40 text-sm mb-2">Message</p>
                  <p className="text-white bg-white/5 p-4 leading-relaxed">
                    {selectedContact.message}
                  </p>
                </div>

                <div className="flex gap-4">
                  <a
                    href={`mailto:${selectedContact.email}`}
                    className="flex-1 bg-gold text-charcoal py-3 font-medium text-center hover:bg-gold/90 transition-colors"
                  >
                    Reply via Email
                  </a>
                  <a
                    href={`https://wa.me/${selectedContact.phone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-[#25D366] text-white py-3 font-medium text-center hover:bg-[#25D366]/90 transition-colors"
                  >
                    Reply via WhatsApp
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-white/40 p-8">
              <div className="text-center">
                <Mail size={40} className="mx-auto mb-4 opacity-50" />
                <p>Select a message to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
